import React, { useMemo } from "react";
import {
  PIPELINE_STAGE_IDS,
  PIPELINE_STAGES,
} from "../constants/pipelineStages";
import {
  formatEngagementPercent,
  formatFollowers,
  formatRateInr,
  getInitials,
} from "../utils/pipelineFormatters";

function PipelineOverviewPanel({ cards, campaignName }) {
  const counts = useMemo(() => {
    return Object.fromEntries(
      PIPELINE_STAGES.map((s) => [s.id, 0])
    );
  }, []);

  const computed = useMemo(() => {
    const stageCounts = { ...counts };
    cards.forEach((c) => {
      if (stageCounts[c.stageId] != null) stageCounts[c.stageId] += 1;
    });

    const contracted = cards.filter(
      (c) => c.stageId === PIPELINE_STAGE_IDS.CONTRACTED
    );
    const confirmed = cards.filter(
      (c) => c.stageId === PIPELINE_STAGE_IDS.CONFIRMED
    );

    const confirmedAndLive = [...contracted, ...confirmed];
    const maxStageCount = Math.max(
      ...PIPELINE_STAGES.map((s) => stageCounts[s.id] ?? 0),
      1
    );

    const influencersInPipeline = cards.length;
    const combinedFollowers = confirmedAndLive.reduce(
      (acc, c) => acc + (Number(c.followers) || 0),
      0
    );
    const spent = confirmedAndLive.reduce(
      (acc, c) => acc + (Number(c.rate) || 0),
      0
    );

    // Simple KPI budget approximation until backend provides real budget/spend.
    const budget = spent > 0 ? Math.round(spent * 1.2) : 250000;

    const topConfirmedAndLive = confirmedAndLive
      .slice()
      .sort((a, b) => (Number(b.rate) || 0) - (Number(a.rate) || 0))
      .slice(0, 2);

    return {
      stageCounts,
      maxStageCount,
      influencersInPipeline,
      confirmedCount: confirmed.length,
      combinedFollowers,
      spent,
      budget,
      topConfirmedAndLive,
      confirmedAndLiveTotal: confirmedAndLive.length,
    };
  }, [cards, counts]);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-[15px] font-semibold text-[#1A1714]">
            Overview
          </h2>
          <p className="text-[13px] text-[#6B6560]">
            {campaignName ? `Campaign: ${campaignName}` : "Select a campaign"}
          </p>
        </div>
      </div>

      {/* KPI cards row */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="rounded-2xl border border-[#E3DFD9] bg-white md:py-4 py-2 px-2 md:px-4 shadow-sm">
          <p className="text-[10px] font-semibold text-[#A09890] uppercase tracking-[0.16em] text-center md:text-left">
            Influencers
          </p>
          <p className="mt-2 text-lg md:text-xl text-center md:text-left font-light text-[#1A1714] tabular-nums font-['DM_Serif_Display',serif]">
            {computed.influencersInPipeline}
          </p>
          <p className="text-[11px] text-[#A09890] mt-1 text-center md:text-left">
            in pipeline
          </p>
        </div>

        <div className="rounded-2xl border border-[#E3DFD9] bg-white md:py-4 py-2 px-2 md:px-4 shadow-sm">
          <p className="text-[10px] font-semibold text-[#A09890] uppercase tracking-[0.16em] text-center md:text-left">
            Confirmed
          </p>
          <p className="mt-2 text-lg md:text-xl text-center md:text-left font-light text-[#1A1714] tabular-nums font-['DM_Serif_Display',serif]">
            {computed.confirmedCount}
          </p>
          <div className="mt-2 flex items-center gap-2 text-center md:text-left">
            <span className="inline-flex items-center rounded-full bg-[#E1F5EE] text-[#1D9E75] px-2 py-0.5 text-[11px] font-semibold border border-[#9FE1CB]">
              ↑{" "}
              {computed.influencersInPipeline > 0
                ? `${Math.round(
                    (computed.confirmedCount / computed.influencersInPipeline) * 100
                  )}%`
                : "0%"}{" "}
              conversion
            </span>
          </div>
        </div>

        <div className="rounded-2xl border border-[#E3DFD9] bg-white md:py-4 py-2 px-2 md:px-4 shadow-sm">
          <p className="text-[10px] font-semibold text-[#A09890] uppercase tracking-[0.16em] text-center md:text-left">
            Combined reach
          </p>
          <p className="mt-2 text-lg md:text-xl text-center md:text-left font-light text-[#1A1714] tabular-nums font-['DM_Serif_Display',serif]">
            {formatFollowers(computed.combinedFollowers)}
          </p>
          <p className="text-[11px] text-[#A09890] mt-1 text-center md:text-left">
            combined followers
          </p>
        </div>

        <div className="rounded-2xl border border-[#E3DFD9] bg-white md:py-4 py-2 px-2 md:px-4 shadow-sm">
          <p className="text-[10px] font-semibold text-[#A09890] uppercase tracking-[0.16em] text-center md:text-left">
            Spent
          </p>
          <p className="mt-2 text-lg md:text-xl text-center md:text-left font-light text-[#1A1714] tabular-nums font-['DM_Serif_Display',serif]">
            {formatRateInr(computed.spent)}
          </p>
          <p className="text-[11px] text-[#A09890] mt-1 text-center md:text-left">
            of {formatRateInr(computed.budget)}
          </p>
        </div>
      </div>

      {/* Lower grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Pipeline funnel */}
        <div className="lg:col-span-2 rounded-xl border border-gray-100 bg-white md:py-4 py-2 px-2 md:px-4 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Pipeline funnel</h3>

          <div className="space-y-4">
            {PIPELINE_STAGES.map((s) => {
              const count = computed.stageCounts[s.id] ?? 0;
              const widthPct = (count / computed.maxStageCount) * 100;

              return (
                <div key={s.id} className="flex items-center gap-3">
                  <div className="w-[130px] shrink-0">
                    <span className="text-xs font-semibold text-gray-700">
                      {s.label}
                    </span>
                  </div>

                  <div className="flex-1">
                    <div className="h-2.5 w-full rounded-full bg-gray-100 border border-gray-100 overflow-hidden">
                      <div
                        className={`h-full ${s.dotClass}`}
                        style={{ width: `${Math.max(widthPct, count ? 3 : 0)}%` }}
                      />
                    </div>
                  </div>

                  <div className="w-8 text-right shrink-0">
                    <span className="text-xs font-bold text-gray-500 tabular-nums">
                      {count}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Contracted & Confirmed list */}
        <div className="rounded-xl border border-gray-100 bg-white md:py-4 py-2 px-2 md:px-4 shadow-sm">
          <h3 className="text-sm font-bold text-gray-900 mb-3">
            Contracted &amp; Confirmed
          </h3>

          <div className="space-y-3">
            {computed.topConfirmedAndLive.map((c) => {
              const stageLabel =
                c.stageId === PIPELINE_STAGE_IDS.CONTRACTED
                  ? "Contracted"
                  : "Confirmed";

              const stagePillClass =
                c.stageId === PIPELINE_STAGE_IDS.CONTRACTED
                  ? "bg-amber-50 text-amber-700 border-amber-100"
                  : "bg-emerald-50 text-emerald-700 border-emerald-100";

              return (
                <div
                  key={c.id}
                  className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 flex items-center justify-between gap-3"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0">
                      <span className="text-xs font-bold text-gray-800">
                        {getInitials(c.name)}
                      </span>
                    </div>

                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-gray-900 truncate">
                        {c.name}
                      </p>
                      <p className="text-[11px] text-gray-500 truncate">
                        {c.handle}
                      </p>
                      <p className="text-[11px] text-gray-500 tabular-nums mt-0.5">
                        {formatFollowers(c.followers)} •{" "}
                        {formatEngagementPercent(c.engagementRate)}
                      </p>
                    </div>
                  </div>

                  <div
                    className={`shrink-0 inline-flex items-center rounded-full px-2 py-1 border text-[11px] font-semibold ${stagePillClass}`}
                  >
                    {stageLabel}
                  </div>
                </div>
              );
            })}

            {computed.topConfirmedAndLive.length === 0 && (
              <div className="text-xs text-gray-500">
                No contracted/confirmed influencers yet.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PipelineOverviewPanel;
